import { useEffect } from "react";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectLoan } from "@/store/selectors";
import { RUSSIAN_LOCALE } from "../../helpers/const";
import { PeriodTypeId } from "../../helpers/helpers";
import {
  getCalculatorSettingsThunk,
  getDefaultCalculatorSettingsThunk,
  sendCalculatorSettings,
} from "../../server/thunks/thunks";
import { LoanActionCreators } from "../../store/action-creators";

const getDefaults = (registration) => {
  if (registration)
    return {
      amount: 0, // отправим на сервер 0, чтобы он взял значение из настроек
      term: 0, // same
      periodTypeId: PeriodTypeId.days,
    };
  return {
    amount: 0, // отправим на сервер 0, чтобы он взял значение из настроек
    term: 0, // same
    periodTypeId: PeriodTypeId.weeks,
  };
};

export const CalculatorType = {
  REGAUTH: 1,
  PRODUCT: 2,
};

export const PriorityType = {
  SUM: 1,
  PERIOD: 2,
};

const isRegistration = (location) => location.pathname === "/registration";

const selectRegAuthCalculatorSettings = (state) =>
  state.registrationReducer?.defaultCalculatorSettings?.settings;

const selectProductCalculatorSettings = (state) =>
  state.newFormReducer.calculatorSettings?.settings;

const selectApplicationId = (state) => state.newFormReducer.applicationId;

// Получаем индекс среднего элемента в списке
const getMiddleIndex = (arr) => Math.floor((arr.length - 1) / 2);
const getMiddle = (arr) => arr[getMiddleIndex(arr)];

// рассчитывает элементы для шкалы суммы
const getSumScaleItem = (calculatorSettings, number) => {
  if (!calculatorSettings) return 0;

  const { sumItems } = calculatorSettings;
  if (!sumItems) return "";

  switch (number) {
    case 0:
      return sumItems[0]?.toLocaleString(RUSSIAN_LOCALE);
    case 1:
      return getMiddle(sumItems)?.toLocaleString(RUSSIAN_LOCALE);
    case 2:
      return sumItems[sumItems.length - 1]?.toLocaleString(RUSSIAN_LOCALE);
    default:
      return 0;
  }
};

// рассчитывает элементы для шкалы период
const getPeriodScaleItem = (calculatorType, calculatorSettings, number) => {
  if (!calculatorSettings || !calculatorSettings.periodTypeItems) return 0;

  const days = calculatorSettings.periodTypeItems.find(
    (p) => p.periodTypeId === PeriodTypeId.days
  )?.periodItems;
  const weeks = calculatorSettings.periodTypeItems.find(
    (p) => p.periodTypeId === PeriodTypeId.weeks
  )?.periodItems;

  switch (number) {
    case 0:
      if ((days && weeks) || (days && !weeks)) {
        return `${days[0]} дн.`;
      }
      if (!days && weeks) {
        return `${weeks[0]} нед.`;
      }
      break;
    case 1:
      if (days && weeks) {
        return `${weeks[0]} нед.`;
      }
      if (days && !weeks) {
        return `${days[0]} дн.`;
      }

      if (!days && weeks) {
        return `${getMiddle(weeks)} нед.`;
      }
      break;

    case 2:
      if (days && weeks) {
        return `${weeks[weeks.length - 1]} нед.`;
      }
      if (days && !weeks) {
        return `${days[days.length - 1]} дн.`;
      }
      if (!days && weeks) {
        return `${weeks[weeks.length - 1]} нед.`;
      }
      break;
    default:
      return 0;
  }
  return 0;
};

const useCalculatorSettings = (calculatorType) => {
  const location = useLocation();
  const registration = isRegistration(location);
  const applicationId = useSelector(selectApplicationId);
  const regAuthCalculatorSettings =
    useSelector(selectRegAuthCalculatorSettings) || {};
  const productCalculatorSettings =
    useSelector(selectProductCalculatorSettings) || {};

  let calculatorSettings = {};

  if (calculatorType === CalculatorType.REGAUTH) {
    calculatorSettings = regAuthCalculatorSettings;
  }

  if (calculatorType === CalculatorType.PRODUCT) {
    calculatorSettings = productCalculatorSettings;
  }

  const applyCalculation = (
    { sum, period, periodTypeId, changedSum },
    { fastPrefetch = false } = {}
  ) => {
    if (calculatorType === CalculatorType.REGAUTH) {
      return getDefaultCalculatorSettingsThunk({
        requestedPeriod: period,
        requestedSum: sum,
        periodTypeId,
        registration,
        changedSum,
      });
    }

    // не отправляем productId (NRF-517)
    return sendCalculatorSettings(
      {
        applicationId,
        offerSum: sum,
        offerPeriod: period,
        priorityType: changedSum ? PriorityType.SUM : PriorityType.PERIOD,
        periodTypeId,
        notSetProductInApplication: false,
      },
      { fastPrefetch }
    );
  };

  return {
    location,
    registration,
    applicationId,
    calculatorSettings,
    applyCalculation,
  };
};

export const useCalculator = (calculatorType) => {
  const [cookies] = useCookies();

  const location = useLocation();

  const dispatch = useDispatch();

  const { registration, applicationId, calculatorSettings, applyCalculation } =
    useCalculatorSettings(calculatorType);
  const { sumItems } = calculatorSettings;
  const amounts = useSelector(selectLoan);
  const isRegistrationPage = location.pathname.startsWith("/registration");

  const days = calculatorSettings?.periodTypeItems?.find(
    (p) => p.periodTypeId === PeriodTypeId.days
  )?.periodItems;
  const weeks = calculatorSettings?.periodTypeItems?.find(
    (p) => p.periodTypeId === PeriodTypeId.weeks
  )?.periodItems;

  // набор доступных значений для выбора суммы
  const loanItems = sumItems
    ? sumItems.map((sum) => ({
        number: sum,
      }))
    : [];

  // набор доступных значений для выбора срока
  let periodItems = [];
  if (days) {
    periodItems = periodItems.concat(
      days.map((day) => ({ number: day, periodTypeId: PeriodTypeId.days }))
    );
  }
  if (weeks) {
    periodItems = periodItems.concat(
      weeks.map((week) => ({
        number: week,
        periodTypeId: PeriodTypeId.weeks,
      }))
    );
  }

  // пересчет с учетом нового срока, выбранного пользователем
  const recalculateOnChangePeriod = (period) => {
    if (!period) return;
    // Эти значения изменятся после ответа от сервера
    // выставив их сразу, чиним фокусировку ползунка по табу. иначе значение ползунка не меняется после ответа от сервера
    dispatch(LoanActionCreators.setTermAmount(period.number));
    dispatch(LoanActionCreators.setPeriodType(period.periodTypeId));

    dispatch(
      applyCalculation({
        sum: amounts.loanAmount,
        period: period.number,
        periodTypeId: period.periodTypeId,
        changedSum: false,
      })
    );
  };

  // пересчет с учетом новой выбранной суммы
  const recalculateOnChangeLoan = (sum) => {
    // Эти значения изменятся после ответа от сервера
    // выставив их сразу, чиним фокусировку ползунка по табу. иначе значение ползунка не меняется после ответа от сервера
    dispatch(LoanActionCreators.setLoanAmount(sum));

    dispatch(
      applyCalculation({
        sum,
        period: amounts.term,
        periodTypeId: amounts.periodTypeId,
        changedSum: true,
      })
    );
  };

  const refetchSettings = () => {
    if (calculatorType === CalculatorType.REGAUTH) {
      return;
    }
    dispatch(
      applyCalculation(
        {
          sum: amounts.loanAmount,
          period: amounts.term,
          periodTypeId: amounts.periodTypeId,
          changedSum: false,
        },
        { fastPrefetch: true }
      )
    );
  };

  // обновление состояния на основе состояния из кук (почему куки?)
  const fetchDefaultCalcSettings = () => {
    // Куки приходят от лендинга
    const {
      amount,
      term,
      periodTypeId: defaultPeriodTypeId,
    } = getDefaults(isRegistrationPage);

    const requestedSum = Number(cookies.amount || amount);
    const requestedPeriod = Number(cookies.term || term);
    let periodTypeId = cookies.periodType || defaultPeriodTypeId;

    if (cookies.product_type && cookies.product_type === "installment") {
      periodTypeId = PeriodTypeId.weeks;
    }
    if (cookies.product_type && cookies.product_type === "pdl") {
      periodTypeId = PeriodTypeId.days;
    }

    dispatch(
      getDefaultCalculatorSettingsThunk({
        requestedSum,
        requestedPeriod,
        periodTypeId,
        registration,
      })
    );
  };

  const isSmartCalculator = !cookies.term;

  useEffect(() => {
    // проверяем куки, если там есть сумма, ставим на калькулятор, если нет, ставим defaultные значения
    if (calculatorType === CalculatorType.REGAUTH) {
      fetchDefaultCalcSettings();
    } else if (!calculatorSettings || !calculatorSettings.sumItems) {
      dispatch(
        getCalculatorSettingsThunk({
          applicationId,
          useSmartCalculator: isSmartCalculator,
        })
      );
    }
  }, [isRegistrationPage, isSmartCalculator]);

  useEffect(() => {
    refetchSettings();
  }, []);

  return {
    days,
    weeks,
    loanItems,
    periodItems,
    calculatorSettings,
    recalculateOnChangeLoan,
    recalculateOnChangePeriod,
    getSumScaleItem: (number) => getSumScaleItem(calculatorSettings, number),
    getPeriodScaleItem: (number) =>
      getPeriodScaleItem(calculatorType, calculatorSettings, number),
  };
};

export const useCalculatorType = () => {
  const { pathname } = useLocation();
  if (
    pathname.startsWith("/registration") ||
    pathname.startsWith("/authorization")
  ) {
    return CalculatorType.REGAUTH;
  }
  return CalculatorType.PRODUCT;
};
